import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to all requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      // Optionally redirect to login
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Helper to format API error details
export const formatApiError = (error) => {
  const detail = error.response?.data?.detail;
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

// Canonical failure handler for save/delete/action catch blocks: logs the raw error
// AND surfaces a visible toast so an operation never fails silently. `message` is a
// human context prefix (e.g. 'Failed to save section'). Returns the formatted string.
export const handleApiError = (error, message) => {
  const formatted = formatApiError(error);
  console.error(`${message}:`, error);
  toast.error(`${message}: ${formatted}`);
  return formatted;
};

// Auth
export const authApi = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
};

// Admin — user management (server-side gated by require_admin)
export const adminApi = {
  listUsers: () => api.get('/admin/users'),
  createUser: (data) => api.post('/admin/users', data),
  updateUser: (id, data) => api.patch(`/admin/users/${id}`, data),
  resetPassword: (id, password) => api.post(`/admin/users/${id}/reset-password`, { password }),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
};

// Projects
export const projectsApi = {
  getAll: () => api.get('/projects'),
  getOne: (id) => api.get(`/projects/${id}`),
  getCategories: () => api.get('/projects/categories'),
  getPipelineStages: (category) => api.get(`/projects/pipeline-stages/${encodeURIComponent(category)}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
};

// Research
export const researchApi = {
  run: (query) => api.post('/research', { query }),
  pollStatus: (jobId) => api.get(`/research/status/${jobId}`),
  detectMode: (query) => api.post('/research/detect-mode', { query }),
  category: (query, count, submode) => api.post('/research/category', { query, count, submode: submode || 'general' }),
  categoryStatus: (jobId) => api.get(`/research/category/status/${jobId}`),
  saveCategory: (targets) => api.post('/research/category/save', targets),
};

// Targets
export const targetsApi = {
  getAll: () => api.get('/targets'),
  getOne: (id) => api.get(`/targets/${id}`),
  create: (data) => api.post('/targets', data),
  delete: (id) => api.delete(`/targets/${id}`),
  bulkDelete: (targetIds) => api.post('/targets/bulk-delete', { target_ids: targetIds }),
  findDuplicates: () => api.get('/targets/duplicates'),
  merge: (keepId, removeId) => api.post('/targets/merge', { keep_id: keepId, remove_id: removeId }),
  parseContext: (targetId, rawText, focus, contentType) => api.post(`/targets/${targetId}/context-import/parse`, { raw_text: rawText, focus, content_type: contentType || 'other' }),
  parseContextStatus: (targetId, jobId) => api.get(`/targets/${targetId}/context-import/parse/status/${jobId}`),
  applyContext: (targetId, updates) => api.post(`/targets/${targetId}/context-import/apply`, updates),
  getImportLog: (targetId) => api.get(`/targets/${targetId}/context-import-log`),
  extractFile: (targetId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/targets/${targetId}/context-import/extract-file`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  updateContacts: (targetId, contacts) => api.put(`/targets/${targetId}/contacts`, { contacts }),
  updateContact: (targetId, index, data) => api.patch(`/targets/${targetId}/contacts/${index}`, data),
  updateContactInfo: (targetId, data) => api.patch(`/targets/${targetId}/contact-info`, data),
  updateSection: (targetId, sectionKey, sectionValue) => api.patch(`/targets/${targetId}/section`, { section_key: sectionKey, section_value: sectionValue }),
  updateOwner: (targetId, owner) => api.patch(`/targets/${targetId}/owner`, { owner }),
  setOverride: (targetId, field, value) => api.patch(`/targets/${targetId}/override`, { field, value }),
  startDeltaScan: (targetId) => api.post(`/targets/${targetId}/delta-scan`),
  getDeltaScanStatus: (targetId, jobId) => api.get(`/targets/${targetId}/delta-scan/status/${jobId}`),
};

// Pipeline
export const pipelineApi = {
  getAll: () => api.get('/pipeline'),
  create: (data) => api.post('/pipeline', data),
  update: (id, data) => api.patch(`/pipeline/${id}`, data),
  delete: (id) => api.delete(`/pipeline/${id}`),
  logContact: (id) => api.post(`/pipeline/${id}/log-contact`),
  resolveSuggestedTemperature: (id, accept) => api.post(`/pipeline/${id}/resolve-suggested-temperature`, { accept }),
  addNote: (id, note) => api.post(`/pipeline/${id}/add-note`, { note }),
  ownerOptions: () => api.get('/pipeline/owner-options'),
  byTarget: (targetId) => api.get(`/pipeline/target/${targetId}`),
  cleanup: () => api.post('/pipeline/cleanup'),
  cleanupHistory: () => api.get('/pipeline/cleanup/history'),
  dealValueDiagnostic: () => api.get('/pipeline/deal-value-diagnostic'),
  wipePreproposalDealValues: () => api.post('/pipeline/admin/wipe-preproposal-deal-values'),
  fieldAudit: () => api.get('/pipeline/admin/field-audit'),
  migrateOwnerCanonical: () => api.post('/pipeline/admin/migrate-owner-canonical'),
  migrateCategories: (apply = false, mapping = {}) => api.post('/pipeline/admin/migrate-categories', { apply, mapping }),
  migrateSchemaKeys: (apply = false) => api.post('/pipeline/admin/migrate-schema-keys', { apply }),
  cleanupVendorValues: (apply = false) => api.post('/pipeline/admin/cleanup-vendor-values', { apply }),
  restoreLiveProjectRefs: (apply = false) => api.post('/pipeline/admin/restore-live-project-refs', { apply }),
};

// Outreach
export const outreachApi = {
  generate: (targetId, projectId, outreachType, priorContext = null, contextType = null) =>
    api.post('/outreach/generate', {
      target_id: targetId,
      project_id: projectId,
      outreach_type: outreachType,
      prior_context: priorContext,
      context_type: contextType,
    }),
  getPriorContext: (targetId) => api.get(`/outreach/prior-context/${targetId}`),
  saveDraft: (data) => api.post('/outreach/drafts', data),
  getDrafts: (pipelineRecordId) =>
    api.get('/outreach/drafts', { params: { pipeline_record_id: pipelineRecordId } }),
  markSent: (draftId) => api.patch(`/outreach/drafts/${draftId}/send`),
};

// Dashboard
export const dashboardApi = {
  getStats: () => api.get('/dashboard/stats'),
  getNeedsAttention: () => api.get('/dashboard/needs-attention'),
  getWeeklySummary: () => api.get('/weekly-summary'),
  generateReEngagement: (pipelineRecordId) => api.post('/dashboard/re-engage', { pipeline_record_id: pipelineRecordId }),
  getTeamActivity: () => api.get('/dashboard/team-activity'),
  getRelationshipHealth: () => api.get('/dashboard/relationship-health'),
  quickAdd: (name, businessLine) => api.post('/dashboard/quick-add', { name, business_line: businessLine }),
  quickAddStatus: (jobId) => api.get(`/dashboard/quick-add/status/${jobId}`),
};

// Events
export const eventsApi = {
  latest: () => api.get('/events/latest'),
  getDiscovered: () => api.get('/events/discovered'),
  startScan: ({ categories, taxonomyOverride } = {}) => api.post('/events/scan', {
    ...(categories && categories.length ? { categories } : {}),
    ...(taxonomyOverride ? { taxonomy_override: taxonomyOverride } : {}),
  }),
  scanEstimate: (categories) => api.get('/events/scan/estimate', {
    params: categories && categories.length ? { categories: categories.join(',') } : {},
  }),
  scanStatus: (jobId) => api.get(`/events/scan/status/${jobId}`),
  saveNotes: (eventId, notes) => api.put(`/events/${eventId}/notes`, { notes }),
  overrideDate: (eventId, eventStartDate) => api.put(`/events/${eventId}/date`, { event_start_date: eventStartDate }),
  likelyTargets: (eventId) => api.get(`/events/${eventId}/likely-targets`),
};

export const grantsApi = {
  getAll: () => api.get('/grants'),
  create: (data) => api.post('/grants', data),
  update: (id, data) => api.put(`/grants/${id}`, data),
  delete: (id) => api.delete(`/grants/${id}`),
  updateStatus: (id, status) => api.patch(`/grants/${id}/status`, { status }),
  exportCsv: () => api.get('/grants/export/csv', { responseType: 'blob' }),
  startScan: (taxonomyOverride) => api.post('/grants/scan', taxonomyOverride ? { taxonomy_override: taxonomyOverride } : {}),
  scanStatus: (jobId) => api.get(`/grants/scan/status/${jobId}`),
  scanLatest: () => api.get('/grants/scan/latest'),
  saveScanResults: (grants) => api.post('/grants/scan/save', { grants }),
  generateBrief: (id) => api.post(`/grants/${id}/brief`),
  briefStatus: (jobId) => api.get(`/grants/brief/status/${jobId}`),
  getDigest: () => api.get('/grants/digest'),
  getMigrationCandidates: () => api.get('/grants/migration-candidates'),
  migrate: (targetId) => api.post(`/grants/migrate/${targetId}`),
  addToPipeline: (grantId) => api.post(`/grants/${grantId}/add-to-pipeline`),
  getSettings: () => api.get('/grants/settings'),
  updateSettings: (recipients, enabled) => api.post('/grants/settings', { recipients, enabled }),
};

// Context Import
export const contextImportApi = {
  parse: (rawText) => api.post('/context-import/parse', { raw_text: rawText }),
  parseStatus: (jobId) => api.get(`/context-import/parse/status/${jobId}`),
  confirm: (data) => api.post('/context-import/confirm', data),
  extractFile: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/context-import/extract-file', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Export
export const exportApi = {
  pipelineCsv: () => api.get('/export/pipeline/csv', { responseType: 'blob' }),
  targetsCsv: () => api.get('/export/targets/csv', { responseType: 'blob' }),
  pipelinePdf: () => api.get('/export/pipeline/pdf', { responseType: 'blob' }),
  targetPdf: (targetId) => api.get(`/export/target/${targetId}/pdf`, { responseType: 'blob' }),
};

// Briefing
export const briefingApi = {
  generate: (targetId) => api.post(`/briefing/generate/${targetId}`),
  downloadPdf: (briefingId) => api.get(`/briefing/${briefingId}/pdf`, { responseType: 'blob' }),
};

export default api;
